import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'

export default function Dashboard({ onNavigate }) {
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0, scheduled: 0 })
  const [recent, setRecent] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const { data: blogs } = await supabase.from('blogs').select('id,title,status,ai_generated,seo_score,created_at,published_at').order('created_at', { ascending: false })
      if (blogs) {
        setStats({
          total: blogs.length,
          published: blogs.filter(b => b.status === 'published').length,
          drafts: blogs.filter(b => b.status === 'draft').length,
          scheduled: blogs.filter(b => b.status === 'scheduled').length,
        })
        setRecent(blogs.slice(0, 6))
      }
      setLoading(false)
    }
    load()
  }, [])

  function formatDate(d) {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  return (
    <div className="page-content">
      <div className="stats-grid">
        {[
          { label: 'Total Blogs', value: stats.total, meta: 'All time' },
          { label: 'Published', value: stats.published, meta: <span className="up">Live on site</span> },
          { label: 'Drafts', value: stats.drafts, meta: 'Awaiting review' },
          { label: 'Scheduled', value: stats.scheduled, meta: 'Queued to publish' },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{loading ? '—' : s.value}</div>
            <div className="stat-meta">{s.meta}</div>
          </div>
        ))}
      </div>

      <div className="section-header">
        <span className="section-title">Recent Blogs</span>
        <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('blogs')}>View all</button>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>SEO Score</th>
              <th>Source</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className="loading-row"><td colSpan={5}><span className="spinner" /></td></tr>
            )}
            {!loading && recent.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text3)' }}>
                No blogs yet. <button style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font)' }} onClick={() => onNavigate('new')}>Create your first blog →</button>
              </td></tr>
            )}
            {recent.map(b => (
              <tr key={b.id}>
                <td className="td-primary td-link" onClick={() => onNavigate('edit', b.id)} style={{ maxWidth: 340 }}>
                  {b.title}
                </td>
                <td><span className={`badge badge-${b.status}`}>{b.status}</span></td>
                <td>
                  <span style={{ color: b.seo_score >= 80 ? 'var(--green)' : b.seo_score >= 50 ? 'var(--amber)' : 'var(--red)', fontWeight: 500 }}>
                    {b.seo_score || 0}/100
                  </span>
                </td>
                <td>{b.ai_generated ? <span className="badge badge-ai">AI</span> : <span style={{ color: 'var(--text3)', fontSize: 12 }}>Manual</span>}</td>
                <td>{formatDate(b.published_at || b.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 28 }}>
        <div className="section-header">
          <span className="section-title">Quick Actions</span>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-accent" onClick={() => onNavigate('new')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Blog
          </button>
          <button className="btn btn-ghost" onClick={() => onNavigate('keywords')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            Keyword Queue
          </button>
          <button className="btn btn-ghost" onClick={() => onNavigate('blogs')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            All Blogs
          </button>
        </div>
      </div>
    </div>
  )
}
